import { PureComponent, Fragment } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { appDispatch, RootState } from 'store/store';
import messages from './Messages';
import Button from 'components/Button';
import { display } from 'components/ErrorView/ErrorDialog';
import notify from 'components/Notify';
import SingleItemAccessTokenEditor from '../../../../../components/SingleItemAccessTokenEditor';
import LoadingCircleIndicator from '../../../../../components/LoadingCircleIndicator/LoadingCircleIndicator';
import {
  deleteSingleItemAccessToken,
  deleteSingleItemAccessTokenFn,
  ISingleItemAccessToken,
  requireSingleItemAccessToken,
  saveSingleItemAccessToken,
  saveSingleItemAccessTokenFn,
} from 'store/tokens/tokensSlice';
import { IProviderItemView } from 'store/providers/providerTypes';
import i18n from '../../../../../i18n';
import { black } from 'utils/style/colors';

interface IStateToProps {
  tokens: Array<ISingleItemAccessToken>;
}

interface IDispatchToProps {
  saveSingleItemAccessToken: (
    siat: ISingleItemAccessToken,
    isUpdate: boolean
  ) => ReturnType<typeof saveSingleItemAccessTokenFn>;
  deleteSingleItemAccessToken: (
    siat: ISingleItemAccessToken
  ) => ReturnType<typeof deleteSingleItemAccessTokenFn>;
  requireSingleItemAccessToken: (itemId: number) => any;
}

interface IOwnProps {
  item: IProviderItemView;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface IComponentStates {
  newToken: boolean;
  loading: boolean;
}

class SingleItemTokenManagerPanel extends PureComponent<
  IComponentProps,
  IComponentStates
> {
  state = {
    newToken: false,
    loading: true,
  };

  handleAddToken = () => {
    this.setState((prevState) => ({ newToken: !prevState.newToken }));
  };

  handleSaveCheckPoint = (token: ISingleItemAccessToken, isUpdate: boolean) => {
    return this.props
      .saveSingleItemAccessToken(token, isUpdate)
      .then(() => {
        notify(messages.tokenSavedSuccessfully(isUpdate));
        if (!isUpdate) {
          this.setState({ newToken: false });
        }
        return Promise.resolve();
      })
      .catch((err: any) => {
        display(err);
        return Promise.reject();
      });
  };

  componentDidMount(): void {
    this.props
      .requireSingleItemAccessToken(this.props.item.id)
      .unwrap()
      .then(() => {
        this.setState({ loading: false });
      });
  }

  handleDeleteCheckPoint = (token: ISingleItemAccessToken) => {
    this.props
      .deleteSingleItemAccessToken(token)
      .then(() => {
        notify(i18n.t('tokenRemoved', { ns: 'documentManager' }) as string);
      })
      .catch((err: any) => {
        display(err);
      });
  };

  render() {
    const { tokens, item } = this.props;
    const { loading } = this.state;
    if (loading) {
      return (
        <div>
          <LoadingCircleIndicator />
        </div>
      );
    }
    return (
      <>
        <Button color="green" onClick={this.handleAddToken}>
          {i18n.t('addAccessLink', { ns: 'documentManager' }) as string}
        </Button>
        <div>
          {this.state.newToken && (
            <SingleItemAccessTokenEditor
              canUpdate={true}
              onCancel={this.handleAddToken}
              onRemove={this.handleDeleteCheckPoint}
              onSave={this.handleSaveCheckPoint}
              token={undefined}
              item={item}
            />
          )}
          {tokens.length > 0 ? (
            <Fragment>
              {tokens.map((token: ISingleItemAccessToken) => (
                <SingleItemAccessTokenEditor
                  key={token.id}
                  canUpdate={true}
                  onRemove={this.handleDeleteCheckPoint}
                  onSave={this.handleSaveCheckPoint}
                  token={token}
                  item={item}
                />
              ))}
            </Fragment>
          ) : (
            <span className="inline-block py-2" style={{ color: black }}>
              {i18n.t('thereIsNoLinksYet', { ns: 'documentManager' }) as string}
            </span>
          )}
        </div>
      </>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const tokens = Object.values(state.tokens.data).filter((token) => {
    return token.itemId === props.item.id;
  });
  return { tokens };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch
) => ({
  saveSingleItemAccessToken: (
    siat: ISingleItemAccessToken,
    isUpdate: boolean
  ) =>
    appDispatch(
      saveSingleItemAccessToken({
        siat,
        isUpdate,
      })
    ),
  deleteSingleItemAccessToken: (siat: ISingleItemAccessToken) =>
    appDispatch(deleteSingleItemAccessToken({ siat })),
  requireSingleItemAccessToken: (itemId: number) =>
    // @ts-expect-error it's ok, needs pure dispatch
    dispatch(requireSingleItemAccessToken({ itemId })),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SingleItemTokenManagerPanel);
